import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ChevronLeft, ChevronRight, Loader2, RefreshCw,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Slider } from '@/components/ui/slider';
import { useLatestFrameStill } from '@/hooks/use-latest-frame-still';
import { useVideoFrameExtractor } from '@/hooks/use-video-frame-extractor';
import { probeMediaMetadata } from '@/lib/layers/probeMediaMetadata';
import { EXPORT_FPS, formatFrameTime } from '@/lib/timeline/exportFrameTiming';
import type { FrameCaptureSources } from '@/lib/quoteCarousel/resolveFrameCapturePlan';
import { frameExtractorUrl } from '@/lib/quoteCarousel/frameExtractorUrl';
import { resolvePickerStartSource } from '@/lib/quoteCarousel/resolvePickerStartSource';
import type { QuoteFrameSource } from '@/lib/quoteCarousel/types';
import { cn } from '@/lib/utils';

const PREVIEW_OPTIONS = {
  canvasWidth: 540,
  canvasHeight: 960,
  quality: 0.8,
  maxCachedFrames: 24,
  sampleFps: EXPORT_FPS,
  fitToSource: true,
};

export interface FramePickerDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  sources: FrameCaptureSources;
  /** Source the dialog opens on (the slide's frame source). */
  initialSource: QuoteFrameSource;
  /** The slide's hand-picked time on `initialSource`; null = automatic. */
  initialTime: number | null;
  /** Automatic capture time of this slide on a source (null = unknown). */
  autoTimeFor: (source: QuoteFrameSource) => number | null;
  onUse: (pick: { source: QuoteFrameSource; time: number }) => void;
  onResetAuto: () => void;
  /** State of the original (source video) URL query; default 'ready'. */
  originalStatus?: 'ready' | 'loading' | 'error';
  /** Refetches the source video query (shown with the 'error' notice). */
  onRetryOriginal?: () => void;
}

const toFrame = (seconds: number) => Math.max(0, Math.round(seconds * EXPORT_FPS));

/**
 * "Escolher frame": scrub the whole original (full source) or edited
 * (render) video frame by frame
 * and pick the still for one slide. Mounted content only while open, so the
 * preview extractor / metadata probe never run on page load.
 */
export const FramePickerDialog: React.FC<FramePickerDialogProps> = ({ open, onOpenChange, ...rest }) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className="sm:max-w-lg" data-testid="frame-picker-dialog">
      {open && <FramePickerBody onClose={() => onOpenChange(false)} {...rest} />}
    </DialogContent>
  </Dialog>
);

function FramePickerBody({
  sources, initialSource, initialTime, autoTimeFor, onUse, onResetAuto, onClose,
  originalStatus = 'ready', onRetryOriginal,
}: Omit<FramePickerDialogProps, 'open' | 'onOpenChange'> & { onClose: () => void }) {
  const { t } = useTranslation();
  const editedAvailable = !!sources.edited;
  const originalAvailable = !!sources.original;
  const startSource = resolvePickerStartSource(initialSource, sources);
  const timeFor = (source: QuoteFrameSource) => (
    source === initialSource && initialTime !== null ? initialTime : autoTimeFor(source) ?? 0
  );

  const [source, setSource] = useState<QuoteFrameSource>(startSource);
  const [frame, setFrame] = useState(() => toFrame(timeFor(startSource)));
  const [durations, setDurations] = useState<Partial<Record<QuoteFrameSource, number>>>({});
  const videoUrl = sources[source];
  const duration = durations[source] ?? 0;
  const maxFrame = duration > 0 ? Math.max(0, Math.ceil(duration * EXPORT_FPS) - 1) : null;
  const clampedFrame = maxFrame === null ? frame : Math.min(frame, maxFrame);
  const time = clampedFrame / EXPORT_FPS;

  // Duration of the chosen video (metadata only): the whole source video for
  // Original, the render for Editado.
  useEffect(() => {
    if (!videoUrl || durations[source] !== undefined) return;
    let cancelled = false;
    void probeMediaMetadata(videoUrl, 'video').then(({ duration: probed }) => {
      if (!cancelled) setDurations((prev) => ({ ...prev, [source]: probed }));
    });
    return () => {
      cancelled = true;
    };
  }, [durations, source, videoUrl]);

  // Live preview: one active decode, only the latest pending request kept.
  // One extractor per source: each keeps its own video element, so a late
  // load of the other URL can never answer for the selected source. Their
  // URLs carry the CORS cache key; the metadata probe (no CORS) does not.
  const { extractFrame: extractOriginal } = useVideoFrameExtractor(frameExtractorUrl(sources.original), PREVIEW_OPTIONS);
  const { extractFrame: extractEdited } = useVideoFrameExtractor(frameExtractorUrl(sources.edited), PREVIEW_OPTIONS);
  const extractFrame = source === 'edited' ? extractEdited : extractOriginal;
  const { still, loading: stillLoading, clear: clearStill } = useLatestFrameStill(extractFrame, time, !!videoUrl);

  const switchSource = (next: QuoteFrameSource) => {
    if (next === source || !sources[next]) return;
    setSource(next);
    clearStill();
    setFrame(toFrame(timeFor(next)));
  };

  const step = (delta: number) => {
    setFrame(() => {
      const target = clampedFrame + delta;
      return Math.max(0, maxFrame === null ? target : Math.min(maxFrame, target));
    });
  };

  return (
    <>
      <DialogHeader>
        <DialogTitle>{t('quoteCarousel.framePicker.title')}</DialogTitle>
        <DialogDescription>{t('quoteCarousel.framePicker.description')}</DialogDescription>
      </DialogHeader>

      <div className="grid grid-cols-2 gap-1 rounded-md bg-muted p-1" role="radiogroup" data-testid="frame-picker-source">
        {(['original', 'edited'] as const).map((option) => {
          const disabled = !sources[option];
          const disabledTitle = option === 'edited'
            ? t('quoteCarousel.framePicker.editedUnavailable')
            : t(originalStatus === 'loading' ? 'quoteCarousel.framePicker.originalLoading' : 'quoteCarousel.framePicker.originalUnavailable');
          return (
            <button
              key={option}
              type="button"
              role="radio"
              aria-checked={source === option}
              disabled={disabled}
              data-testid={`frame-picker-source-${option}`}
              title={disabled ? disabledTitle : undefined}
              onClick={() => switchSource(option)}
              className={cn(
                'rounded px-2 py-1.5 text-xs font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-50',
                source === option
                  ? 'bg-background text-foreground shadow-sm'
                  : 'text-muted-foreground hover:text-foreground',
              )}
            >
              {t(`quoteCarousel.frameSource.${option}`)}
            </button>
          );
        })}
      </div>
      {!editedAvailable && (
        <p className="text-xs text-muted-foreground" data-testid="frame-picker-edited-unavailable">
          {t('quoteCarousel.framePicker.editedUnavailable')}
        </p>
      )}
      {!originalAvailable && originalStatus === 'error' && (
        <div
          className="flex items-center justify-between gap-2 rounded-md border border-destructive/40 bg-destructive/10 p-2 text-xs text-destructive"
          data-testid="frame-picker-original-error"
        >
          <span>{t('quoteCarousel.framePicker.originalLoadError')}</span>
          {onRetryOriginal && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="h-7 shrink-0 text-xs"
              data-testid="frame-picker-original-retry"
              onClick={onRetryOriginal}
            >
              <RefreshCw className="mr-1 h-3.5 w-3.5" />
              {t('quoteCarousel.retry')}
            </Button>
          )}
        </div>
      )}

      <div className="relative mx-auto flex aspect-[9/16] max-h-[45vh] w-full items-center justify-center overflow-hidden rounded-md bg-black">
        {still && (
          <img
            src={still}
            alt={t('quoteCarousel.framePicker.previewAlt')}
            className="h-full w-full object-contain"
            data-testid="frame-picker-preview"
          />
        )}
        {(stillLoading || !still) && (
          <Loader2 className="absolute h-6 w-6 animate-spin text-white/70" aria-hidden="true" />
        )}
      </div>

      <div className="space-y-2">
        <Slider
          data-testid="frame-picker-slider"
          min={0}
          max={maxFrame ?? Math.max(clampedFrame, 1)}
          step={1}
          value={[clampedFrame]}
          disabled={maxFrame === null}
          onValueChange={([value]) => setFrame(value)}
          thumbProps={{ 'aria-label': t('quoteCarousel.framePicker.scrubber') }}
        />
        <div className="flex items-center justify-between gap-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            data-testid="frame-picker-prev"
            aria-label={t('quoteCarousel.framePicker.prevFrame')}
            disabled={clampedFrame <= 0}
            onClick={() => step(-1)}
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <span className="font-mono text-xs tabular-nums text-muted-foreground" data-testid="frame-picker-time">
            {formatFrameTime(time)}
            {duration > 0 ? ` / ${formatFrameTime(duration)}` : ''}
          </span>
          <Button
            type="button"
            variant="outline"
            size="sm"
            data-testid="frame-picker-next"
            aria-label={t('quoteCarousel.framePicker.nextFrame')}
            disabled={maxFrame !== null && clampedFrame >= maxFrame}
            onClick={() => step(1)}
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      </div>

      <DialogFooter className="gap-2 sm:justify-between sm:gap-0">
        <Button
          type="button"
          variant="ghost"
          data-testid="frame-picker-auto"
          onClick={() => {
            onResetAuto();
            onClose();
          }}
        >
          {t('quoteCarousel.framePicker.resetAuto')}
        </Button>
        <div className="flex gap-2">
          <Button type="button" variant="secondary" data-testid="frame-picker-cancel" onClick={onClose}>
            {t('quoteCarousel.framePicker.cancel')}
          </Button>
          <Button
            type="button"
            data-testid="frame-picker-use"
            disabled={!videoUrl}
            onClick={() => {
              onUse({ source, time });
              onClose();
            }}
          >
            {t('quoteCarousel.framePicker.use')}
          </Button>
        </div>
      </DialogFooter>
    </>
  );
}

export default FramePickerDialog;
